import React from 'react';
import { cva } from "class-variance-authority";
import { cn } from "../../utils/cn";
import Icon from '../AppIcon';
import { Spinner } from './LoadingStates';

// ===== ENHANCED BUTTON VARIANTS SYSTEM ===== //
const enhancedButtonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap font-medium ring-offset-background transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-95",
  {
    variants: {
      variant: {
        // Primary variants
        primary: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-sm hover:shadow-md",
        accent: "bg-accent text-accent-foreground hover:bg-accent/90 shadow-sm hover:shadow-md",
        
        // Outline variants
        outline: "border-2 border-input bg-background hover:bg-accent hover:text-accent-foreground",
        "outline-primary": "border-2 border-primary text-primary bg-background hover:bg-primary hover:text-primary-foreground",
        "outline-accent": "border-2 border-accent text-accent bg-background hover:bg-accent hover:text-accent-foreground",
        
        // Ghost variants
        ghost: "hover:bg-accent hover:text-accent-foreground",
        "ghost-primary": "text-primary hover:bg-primary/10",
        "ghost-accent": "text-accent hover:bg-accent/10",
        
        // Status variants
        success: "bg-success text-success-foreground hover:bg-success/90 shadow-sm hover:shadow-md",
        warning: "bg-warning text-warning-foreground hover:bg-warning/90 shadow-sm hover:shadow-md",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm hover:shadow-md",
        
        // Special variants
        gradient: "bg-gradient-to-r from-primary to-accent text-primary-foreground hover:from-primary/90 hover:to-accent/90 shadow-md hover:shadow-lg",
        link: "text-primary underline-offset-4 hover:underline",
        
        // CTA variants
        cta: "bg-gradient-to-r from-accent to-primary text-primary-foreground font-semibold shadow-lg hover:shadow-xl hover:from-accent/90 hover:to-primary/90 transform hover:scale-105",
        "cta-outline": "border-2 border-accent text-accent bg-background hover:bg-gradient-to-r hover:from-accent/10 hover:to-primary/10 hover:border-primary",
      },
      size: {
        xs: "h-8 px-2 text-xs rounded-md",
        sm: "h-9 px-3 text-sm rounded-md",
        default: "h-11 px-4 py-2 text-sm rounded-lg", // 44px touch target
        md: "h-11 px-6 py-2 text-sm rounded-lg",
        lg: "h-12 px-8 text-base rounded-lg", // 48px
        xl: "h-14 px-10 text-lg rounded-xl", // 56px
        icon: "h-11 w-11 rounded-lg", // 44px x 44px
        "icon-sm": "h-9 w-9 rounded-md",
        "icon-lg": "h-12 w-12 rounded-lg"
      },
      fullWidth: {
        true: "w-full",
        false: ""
      },
      elevated: {
        true: "shadow-lg hover:shadow-xl",
        false: ""
      }
    },
    compoundVariants: [
      {
        variant: ["primary", "secondary", "accent", "success", "warning", "destructive"],
        elevated: true,
        class: "shadow-lg hover:shadow-xl"
      },
      {
        variant: "gradient",
        elevated: true,
        class: "shadow-xl hover:shadow-2xl"
      },
      {
        variant: "cta",
        elevated: true,
        class: "shadow-xl hover:shadow-2xl"
      }
    ],
    defaultVariants: {
      variant: "primary",
      size: "default",
      fullWidth: false,
      elevated: false
    }
  }
);

// ===== ENHANCED BUTTON COMPONENT ===== //
export const EnhancedButton = React.forwardRef(({
  className,
  variant,
  size,
  fullWidth,
  elevated,
  loading = false,
  loadingText,
  disabled = false,
  children,
  iconName,
  iconPosition = 'left',
  iconSize,
  badge,
  tooltip,
  'aria-label': ariaLabel,
  asChild = false,
  ...props
}, ref) => {
  const Comp = asChild ? "span" : "button";

  // Icon size mapping
  const iconSizeMap = {
    xs: 14,
    sm: 16,
    default: 18,
    md: 18,
    lg: 20,
    xl: 22,
    icon: 18,
    "icon-sm": 16,
    "icon-lg": 20
  };

  const calculatedIconSize = iconSize || iconSizeMap[size] || 18;

  // Loading state
  const isLoading = loading;
  const isDisabled = disabled || loading;

  // Generate aria-label
  const getAriaLabel = () => {
    if (ariaLabel) return ariaLabel;
    if (loading) return loadingText || `${children} - Loading`;
    if (size?.includes('icon') && !children) {
      return iconName ? `${iconName} button` : 'Button';
    }
    return undefined;
  };

  const renderIcon = () => {
    if (!iconName) return null;
    
    return (
      <Icon
        name={iconName}
        size={calculatedIconSize}
        className={cn(
          children && iconPosition === 'left' && "mr-2",
          children && iconPosition === 'right' && "ml-2"
        )}
        aria-hidden="true"
      />
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <>
          <Spinner size={size === 'xs' ? 'sm' : 'medium'} />
          <span>{loadingText || children || 'Loading...'}</span>
        </>
      );
    }

    return (
      <>
        {iconPosition === 'left' && renderIcon()}
        {children}
        {iconPosition === 'right' && renderIcon()}
        {badge && (
          <span className="ml-2 px-1.5 py-0.5 bg-background text-xs rounded-full">
            {badge}
          </span>
        )}
      </>
    );
  };

  return (
    <Comp
      ref={ref}
      className={cn(
        enhancedButtonVariants({ variant, size, fullWidth, elevated }),
        className
      )}
      disabled={isDisabled}
      aria-label={getAriaLabel()}
      aria-disabled={isDisabled}
      title={tooltip}
      {...props}
    >
      {renderContent()}
    </Comp>
  );
});

EnhancedButton.displayName = "EnhancedButton";

// ===== SPECIALIZED BUTTON COMPONENTS ===== //

// ===== CTA BUTTON ===== //
export const CTAButton = React.forwardRef(({ 
  children, 
  className,
  ...props 
}, ref) => (
  <EnhancedButton
    ref={ref}
    variant="cta"
    size="lg"
    elevated
    className={cn("font-semibold", className)}
    {...props}
  >
    {children}
  </EnhancedButton>
));

CTAButton.displayName = "CTAButton";

// ===== ICON BUTTON ===== //
export const IconButton = React.forwardRef(({ 
  icon,
  size = "icon",
  variant = "ghost",
  className,
  ...props 
}, ref) => (
  <EnhancedButton
    ref={ref}
    variant={variant}
    size={size}
    iconName={icon}
    className={className}
    {...props}
  />
));

IconButton.displayName = "IconButton";

// ===== FLOATING ACTION BUTTON ===== //
export const FloatingActionButton = React.forwardRef(({ 
  icon,
  className,
  ...props 
}, ref) => (
  <EnhancedButton
    ref={ref}
    variant="primary"
    size="icon-lg"
    elevated
    iconName={icon}
    className={cn(
      "fixed bottom-6 right-6 z-50 rounded-full shadow-xl hover:shadow-2xl",
      className
    )}
    {...props}
  />
));

FloatingActionButton.displayName = "FloatingActionButton";

// ===== BUTTON GROUP ===== //
export const ButtonGroup = ({ 
  children, 
  orientation = "horizontal",
  variant = "outline",
  size = "default",
  className,
  ...props 
}) => {
  const orientationClasses = {
    horizontal: "flex-row",
    vertical: "flex-col"
  };

  return (
    <div
      className={cn(
        "inline-flex",
        orientationClasses[orientation],
        orientation === "horizontal" ? "divide-x" : "divide-y",
        "divide-border rounded-lg border border-input overflow-hidden",
        className
      )}
      role="group"
      {...props}
    >
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            variant: child.props.variant || variant,
            size: child.props.size || size,
            className: cn(
              child.props.className,
              "rounded-none border-0 focus:z-10 focus:ring-0",
              orientation === "horizontal" 
                ? index === 0 ? "rounded-l-lg" : index === React.Children.count(children) - 1 ? "rounded-r-lg" : ""
                : index === 0 ? "rounded-t-lg" : index === React.Children.count(children) - 1 ? "rounded-b-lg" : ""
            )
          });
        }
        return child;
      })}
    </div>
  );
};

// ===== BUTTON WITH DROPDOWN ===== //
export const DropdownButton = React.forwardRef(({ 
  children,
  dropdownContent,
  open = false,
  onOpenChange,
  className,
  ...props 
}, ref) => {
  return (
    <div className="relative inline-block">
      <EnhancedButton
        ref={ref}
        iconName="ChevronDown"
        iconPosition="right"
        className={cn("pr-3", className)}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => onOpenChange && onOpenChange(!open)}
        {...props}
      >
        {children}
      </EnhancedButton>
      
      {open && dropdownContent && (
        <div className="absolute top-full left-0 mt-2 w-full min-w-[200px] bg-background border border-border rounded-lg shadow-lg z-50">
          {dropdownContent}
        </div>
      )}
    </div>
  );
});

DropdownButton.displayName = "DropdownButton";

// ===== TOGGLE BUTTON ===== //
export const ToggleButton = React.forwardRef(({ 
  pressed = false,
  onPressedChange,
  variant = "outline",
  children,
  className,
  ...props 
}, ref) => (
  <EnhancedButton
    ref={ref}
    variant={pressed ? "primary" : variant}
    className={cn(
      "data-[pressed=true]:bg-primary data-[pressed=true]:text-primary-foreground",
      className
    )}
    data-pressed={pressed}
    aria-pressed={pressed}
    onClick={() => onPressedChange && onPressedChange(!pressed)}
    {...props}
  >
    {children}
  </EnhancedButton>
));

ToggleButton.displayName = "ToggleButton";

// Export everything
export default EnhancedButton;
